import React, { useState, useEffect, useCallback, useMemo } from "react"; 
import { toast } from "react-toastify";
import { 
  CheckCircle2, AlertCircle, Calendar, ArrowRight, ShieldCheck, 
  HelpCircle, Star, Sparkles, Award, TrendingUp, FileText, ChevronRight, Ban
} from "lucide-react";
import api from "../../common/apiClient";
import SummaryApi from "../../common/SummaryApi";
import PageLoader from "../../helpers/PageLoader";
import { FaArrowLeft } from "react-icons/fa";

// Helper to format system DB cycle keys to user-friendly labels
const formatCycleDisplay = (cycle) => {
  const mapping = {
    "1_WEEK": "Weekly",
    "2_WEEKS": "Bi-weekly",
    "1_MONTH": "Monthly",
    "2_MONTHS": "Bi-monthly",
    "3_MONTHS": "Quarterly",
    "6_MONTHS": "Half-Yearly",
    "12_MONTHS": "Annual"
  };
  return mapping[cycle] || cycle;
};

export default function KraDashboard({ user }) {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [kraData, setKraData] = useState(null); // Goal assignment data
  
  // Creates historical years range dynamically based on current year
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(() => {
    const savedYear = sessionStorage.getItem("kra_selected_year");
    return savedYear ? parseInt(savedYear, 10) : currentYear;
  });

  // Generates a continuous list of years starting from 2025 up to the current calendar year
  const availableYears = useMemo(() => {
    const years = [];
    const startYear = 2025; 
    const currentYear = new Date().getFullYear(); 

    for (let y = currentYear; y >= startYear; y--) {
      years.push(y);
    }
    return years;
  }, []); 

  // Persist state inside sessionStorage to prevent losing active view on browser reloads
  const [viewState, setViewState] = useState(() => {
    return sessionStorage.getItem("kra_view_state") || "overview";
  });

  // Signature check states
  const [declared1, setDeclared1] = useState(false);
  const [declared2, setDeclared2] = useState(false);
  const [employeeComment, setEmployeeComment] = useState("");

  // Save the active view state & selected year to sessionStorage
  useEffect(() => {
    sessionStorage.setItem("kra_view_state", viewState);
  }, [viewState]);

  useEffect(() => {
    sessionStorage.setItem("kra_selected_year", selectedYear.toString());
  }, [selectedYear]);

  // SelectedYear is passed dynamically in API call
  const loadDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(`${SummaryApi.getMyKra.url}?year=${selectedYear}`);
      
      if (response.data?.success && response.data?.data) {
        const data = response.data.data;
        setKraData(data);
        
        if (data.employeeStatus === "PENDING_ACKNOWLEDGEMENT") {
          const storedState = sessionStorage.getItem("kra_view_state");
          if (storedState === "review_details") {
            setViewState("review_details");
          } else {
            setViewState("alert_overlay");
          }
        } else {
          setViewState("overview");
        }
      } else {
        setKraData(null);
        setViewState("overview");
      }
    } catch (err) {
      console.error("Failed to load assigned KRA benchmarks.", err);
      setKraData(null);
      setViewState("overview");
    } finally {
      setLoading(false);
    }
  }, [selectedYear]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  // Dynamic progress calculations
  const overallProgress = useMemo(() => {
    if (!kraData) return 0;

    if (typeof kraData.overallProgress === "number") return kraData.overallProgress;
    if (typeof kraData.progress === "number") return kraData.progress;
    if (typeof kraData.completionPercentage === "number") return kraData.completionPercentage;

    if (kraData.kras && kraData.kras.length > 0) {
      const hasProgressField = kraData.kras.some(kra => typeof kra.progress === "number" || typeof kra.completion === "number");
      if (hasProgressField) {
        const weightedSum = kraData.kras.reduce((acc, kra) => {
          const progressVal = kra.progress ?? kra.completion ?? 0;
          const weight = kra.weightage ?? 0;
          return acc + (progressVal * weight);
        }, 0);

        const totalWeight = kraData.kras.reduce((acc, kra) => acc + (kra.weightage ?? 0), 0) || 100;
        return Math.round(weightedSum / totalWeight);
      }
    }

    return 0; 
  }, [kraData]);

  const handleAcknowledge = async (statusType) => {
    if (statusType === "ACKNOWLEDGED" && (!declared1 || !declared2)) {
      return toast.error("Please confirm both declaration checkboxes before acknowledging.");
    }

    if (statusType === "DISPUTED" && !employeeComment.trim()) {
      return toast.error("Please provide your reasons in the comments box before submitting a dispute.");
    }

    setSubmitting(true);
    try {
      const response = await api.put(SummaryApi.employeeAcknowledgeKra.url, {
        year: selectedYear, 
        employeeStatus: statusType, 
        employeeComment: employeeComment
      });

      if (response.data?.success) {
        toast.success(`KRA goals successfully ${statusType.toLowerCase()}!`);
        sessionStorage.setItem("kra_view_state", "overview");
        loadDashboardData(); 
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to submit goal acknowledgement.");
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric"
    });
  };

  const calculateRemainingDays = (endDateStr) => {
    if (!endDateStr) return "0 Days";
    const diffTime = new Date(endDateStr) - new Date();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? `${diffDays} Days` : "Ended";
  };

  if (loading) return <PageLoader />;

  // ==========================================
  // YEAR SELECTOR COMPONENT
  // ==========================================
  const YearFilter = () => (
    <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-1.5 shadow-sm">
      <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Year:</span>
      <select 
        value={selectedYear}
        onChange={(e) => {
          setSelectedYear(parseInt(e.target.value, 10));
          sessionStorage.setItem("kra_view_state", "overview");
        }}
        className="text-xs font-bold text-gray-800 bg-transparent outline-none cursor-pointer focus:ring-0"
      >
        {availableYears.map((yr) => (
          <option key={yr} value={yr}>FY {yr}</option>
        ))}
      </select>
    </div>
  );

  // ==========================================
  // SCENARIO A: NO GOALS ASSIGNED YET
  // ==========================================
  if (!kraData) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
          <div>
            <h2 className="text-xl font-bold text-gray-900 leading-tight text-left">Welcome, {user?.firstName} {user?.lastName}!</h2>
            <p className="text-xs text-gray-400 mt-1 text-left">Track your KRA goals and active performance evaluation cycles.</p>
          </div>
          <YearFilter />
        </div>

        <div className="flex flex-col items-center justify-center min-h-[480px] bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center animate-in fade-in duration-500">
          <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mb-6 text-primary ring-8 ring-red-50/50">
            <Ban className="w-8 h-8" />
          </div>
          
          <h2 className="text-lg font-bold text-gray-800 uppercase tracking-widest">No Active Goals Assigned</h2>
          <p className="text-gray-400 text-xs font-semibold max-w-sm mx-auto mt-2 leading-relaxed">
            Your performance benchmark framework has not been initialized or assigned for the selected review cycle ({selectedYear}) yet.
          </p>

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-2xl w-full text-left">
            <div className="bg-gray-50 border border-gray-100 p-4 rounded-xl relative">
              <span className="absolute -top-3 left-4 bg-primary text-white text-[10px] font-bold w-6 h-6 rounded-full flex items-center justify-center shadow-sm">1</span>
              <p className="text-xs font-bold text-gray-800 mt-1">Goal Definition</p>
              <p className="text-[10px] text-gray-400 mt-1">HR and your reporting manager will define your Key Result Areas (KRAs).</p>
            </div>
            <div className="bg-gray-50 border border-gray-100 p-4 rounded-xl relative">
              <span className="absolute -top-3 left-4 bg-primary text-white text-[10px] font-bold w-6 h-6 rounded-full flex items-center justify-center shadow-sm">2</span>
              <p className="text-xs font-bold text-gray-800 mt-1">Acknowledge Goals</p>
              <p className="text-[10px] text-gray-400 mt-1">You will receive an alert to review, sign, and accept your assigned targets.</p>
            </div>
            <div className="bg-gray-50 border border-gray-100 p-4 rounded-xl relative">
              <span className="absolute -top-3 left-4 bg-primary text-white text-[10px] font-bold w-6 h-6 rounded-full flex items-center justify-center shadow-sm">3</span>
              <p className="text-xs font-bold text-gray-800 mt-1">Evaluation Cycle</p>
              <p className="text-[10px] text-gray-400 mt-1">Perform, log achievements, and participate in scheduled reviews.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ==========================================
  // SCENARIO B (Step 1): GOALS ASSIGNED ALERT OVERLAY
  // ==========================================
  if (viewState === "alert_overlay" && kraData) {
    return (
      <div className="bg-white border border-gray-100 rounded-xl shadow-sm overflow-hidden animate-scale-in p-1">
        <div className="relative rounded-t-lg bg-gradient-to-r from-red-50 to-blue-50 p-6 md:p-10 flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="space-y-3 max-w-lg text-center md:text-left">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center text-primary mx-auto md:mx-0">
              <Sparkles className="w-5 h-5 text-primary" />
            </div>
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 leading-tight">New Performance Goals Assigned</h2>
            <p className="text-xs text-gray-500 leading-relaxed">
              Your Key Result Areas (KRAs) for the selected period ({selectedYear}) have been finalized and assigned to your profile.
            </p>
          </div>
          
          <div className="flex flex-col gap-2 shrink-0 items-end">
            <YearFilter />
          </div>
        </div>

        <div className="p-6 md:p-8 space-y-6 text-center">
          <div className="w-16 h-16 bg-blue-50 border border-blue-200 rounded-xl flex items-center justify-center mx-auto text-blue-500">
            <CheckCircle2 className="w-8 h-8" />
          </div>

          <div className="space-y-2">
            <h3 className="text-lg font-bold text-gray-800 leading-tight">New Goals Require Review</h3>
            <p className="text-xs text-gray-400 max-w-sm mx-auto leading-relaxed">
              You must review and digitally sign your target configurations. This serves as the benchmark for your appraisals.
            </p>
          </div>

          <div className="bg-gray-50 border border-gray-100 rounded-3xl p-5 max-w-2xl mx-auto grid grid-cols-3 gap-2 text-center text-xs">
            <div>
              <p className="text-[10px] font-bold text-gray-400 uppercase">Review Period</p>
              <p className="font-bold text-gray-800 mt-1">FY {kraData.year} - {formatCycleDisplay(kraData.reviewCycle)}</p>
            </div>
            <div className="border-x">
              <p className="text-[10px] font-bold text-gray-400 uppercase">Start Date</p>
              <p className="font-bold text-gray-800 mt-1">{formatDate(kraData.startDate)}</p>
            </div>
            <div>
              <p className="text-[10px] font-bold text-gray-400 uppercase">End Date</p>
              <p className="font-bold text-gray-800 mt-1">{formatDate(kraData.endDate)}</p>
            </div>
          </div>

          <div className="flex items-center justify-center pt-4 border-t border-gray-100">
            <button 
              onClick={() => setViewState("review_details")}
              className="bg-primary hover:bg-secondary text-white font-bold text-xs uppercase px-12 py-3.5 rounded-lg shadow-lg shadow-red-100 transition-all flex items-center gap-1.5"
            >
              Review KRA <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="bg-blue-50/50 p-3 text-center text-[10px] font-bold text-blue-600 flex items-center justify-center gap-1.5">
          <AlertCircle className="w-4 h-4" /> You have 5 working days to acknowledge these goals.
        </div>
      </div>
    );
  }

  // ==========================================
  // SCENARIO B (Step 2) & READ-ONLY MODE
  // ==========================================
  if ((viewState === "review_details" || viewState === "review_readonly") && kraData) {
    const isReadOnly = viewState === "review_readonly";

    return (
      <div className="space-y-6 animate-scale-in p-2">
        <div className="flex justify-between items-start border-b pb-4">
          <div>
            <div className="text-xs md:text-sm text-gray-400 font-medium">
              <span 
                onClick={() => {
                  if (isReadOnly) {
                    setViewState("overview");
                  } else {
                    setViewState("alert_overlay");
                  }
                }} 
                className="text-primary cursor-pointer hover:text-secondary transition-colors focus:outline-none"
              >
                KRA Management
              </span> &gt; <span className="text-secondary font-bold">Review & Acknowledge</span>
            </div>
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 tracking-tight mt-1 text-left">
              {isReadOnly ? "KRA Mapped Framework" : "KRA Review & Acknowledge"}
            </h2>
            <p className="text-xs text-gray-400 mt-0.5 text-left">
              {isReadOnly 
                ? "Your active Key Result Areas (KRAs) and completed commitments for the year."
                : "Review your Key Result Areas for the upcoming performance cycle and confirm your commitment."}
            </p>
          </div>
          <div className="flex items-center gap-4">
            <YearFilter />
            <button 
              onClick={() => {
                if (isReadOnly) {
                  setViewState("overview");
                } else {
                  setViewState("alert_overlay");
                }
              }} 
              className="flex items-center gap-2 text-xs text-secondary hover:text-primary font-bold animate-pulse"
            >
             <FaArrowLeft size={16} /> Back
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
          {/* Left Panel: Assigned KRA list */}
          <div className="lg:col-span-2 space-y-4">
            {kraData.kras?.map((kra, idx) => (
              <div key={kra.kraId || idx} className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm hover:border-red-200 transition-all space-y-4 text-left">
                <div className="flex justify-between items-center">
                  <span className="bg-red-50 text-primary border border-red-100 px-3 py-1 rounded-full font-bold text-[10px] uppercase">
                    {kra.category || "General"}
                  </span>
                  <span className="text-sm font-bold text-primary">
                    Weight {kra.weightage}%
                  </span>
                </div>

                <div className="space-y-2">
                  <h3 className="font-bold text-gray-900 text-base">{kra.name}</h3>
                  <p className="text-xs text-gray-500 leading-relaxed">{kra.description}</p>
                </div>

                {/* Simplified target layout with dynamic fallback chain */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-3 border-t text-[11px] text-gray-400 font-medium bg-slate-50/40 p-3 rounded-lg">
                  <div className="flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5 shrink-0" />
                    <span>Timeline: {formatDate(kraData.startDate)} - {formatDate(kraData.endDate)}</span>
                  </div>
                  <div className="flex items-center gap-1.5 sm:justify-end">
                    <Star className="w-3.5 h-3.5 shrink-0" />
                    <span>Review Frequency: {formatCycleDisplay(kra.reviewCycle)}</span>
                  </div>
                  {/* ✅ Upgraded targets panel inside signature review cards to clearly view Goal/Metric direction */}
                  <div className="sm:col-span-2 mt-2 pt-2 border-t border-dashed border-gray-200 flex flex-wrap justify-between items-center gap-2">
                    <div>
                      <span className="font-bold text-primary">Target:</span>{" "}
                      <span className="text-gray-950 font-black">
                        {kra.targetValue || kra.monthlyTarget || kra.yearlyTarget || "N/A"}
                      </span>
                    </div>
                    <div className="text-[10px] bg-slate-100 text-slate-600 font-bold px-2 py-0.5 rounded border border-slate-200 uppercase tracking-wider">
                      Goal Direction: <span className="text-primary">{kra.metricType === "LOWER_IS_BETTER" ? "Lower is Better" : "Higher is Better"}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Right Panel: Side Action Card */}
          <div className="space-y-6">
            <div className="bg-white border rounded-xl p-6 shadow-sm space-y-5">
              <div className="border-b pb-2">
                <p className="text-[10px] font-bold text-gray-400 uppercase text-left">Review Summary</p>
                <div className="grid grid-cols-2 gap-y-3 text-xs mt-3">
                  <span className="text-gray-400 text-left">Review Period:</span>
                  <span className="font-bold text-gray-800 text-right">FY {kraData.year}</span>
                  <span className="text-gray-400 text-left">Assigned By:</span>
                  <span className="font-bold text-gray-800 text-right truncate">
                    {kraData.assignedBy?.firstName} {kraData.assignedBy?.lastName}
                  </span>
                  <span className="text-gray-400 text-left">Total Weight:</span>
                  <span className="font-bold text-primary text-right">100%</span>
                </div>
              </div>

              <div className="space-y-3">
                <p className="text-[10px] font-bold text-gray-400 uppercase text-left">Declaration</p>
                <label className="flex items-start gap-3 p-3 bg-gray-50 border rounded-lg cursor-pointer hover:border-red-300 transition-colors text-left">
                  <input 
                    type="checkbox" 
                    checked={isReadOnly ? true : declared1} 
                    disabled={isReadOnly}
                    onChange={() => !isReadOnly && setDeclared1(!declared1)} 
                    className="accent-primary h-4.5 w-4.5 shrink-0 mt-0.5" 
                  />
                  <span className="text-xs text-gray-600 font-medium leading-relaxed">
                    I understand my responsibilities and the performance standards expected.
                  </span>
                </label>

                <label className="flex items-start gap-3 p-3 bg-gray-50 border rounded-lg cursor-pointer hover:border-red-300 transition-colors text-left">
                  <input 
                    type="checkbox" 
                    checked={isReadOnly ? true : declared2} 
                    disabled={isReadOnly}
                    onChange={() => !isReadOnly && setDeclared2(!declared2)} 
                    className="accent-primary h-4.5 w-4.5 shrink-0 mt-0.5" 
                  />
                  <span className="text-xs text-gray-600 font-medium leading-relaxed">
                    I have reviewed the metrics and timelines associated with each KRA.
                  </span>
                </label>
              </div>

              <div className="text-left">
                <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Acknowledgement Comments</label>
                <textarea
                  value={isReadOnly ? (kraData.employeeComment || "Accepted") : employeeComment}
                  readOnly={isReadOnly}
                  disabled={isReadOnly}
                  onChange={(e) => !isReadOnly && setEmployeeComment(e.target.value)}
                  placeholder="Optional notes or remarks on your goals..."
                  rows={2}
                  className={`w-full border rounded-lg p-3 text-xs outline-none resize-none ${isReadOnly ? "bg-gray-50 text-gray-500" : "focus:ring-1 focus:ring-red-400"}`}
                />
                {!isReadOnly && (
                  <p className="text-[10px] text-amber-600 font-bold mt-1 leading-normal">
                    * If disputing the goals, you must write down your specific reasons in the comments box above.
                  </p>
                )}
              </div>

              <div className="space-y-3 pt-2">
                {isReadOnly ? (
                  <div className="bg-secondary text-white rounded-lg p-3.5 text-center text-xs font-bold uppercase flex items-center justify-center gap-1.5">
                    <CheckCircle2 className="w-4 h-4" /> Locked & Acknowledged
                  </div>
                ) : (
                  <>
                    <button
                      onClick={() => handleAcknowledge("ACKNOWLEDGED")}
                      disabled={submitting || !declared1 || !declared2}
                      className={`w-full py-3 rounded-lg font-bold text-xs uppercase transition-all shadow-lg ${
                        submitting || !declared1 || !declared2
                          ? "bg-gray-300 text-gray-400 cursor-not-allowed shadow-none"
                          : "bg-primary text-white hover:shadow-red-100 hover:bg-secondary"
                      }`}
                    >
                      {submitting ? "Signing..." : "Acknowledge"}
                    </button>
                    
                    <button
                      onClick={() => handleAcknowledge("DISPUTED")}
                      disabled={submitting}
                      className="w-full bg-white border border-gray-200 text-gray-500 hover:text-primary font-bold py-3 rounded-lg text-xs uppercase transition-all"
                    >
                      Dispute Assignment
                    </button>
                  </>
                )}
                <p className="text-[10px] text-gray-400 text-center leading-relaxed font-semibold">
                  This action is digitally signed and recorded in your permanent record.
                </p>
              </div>
            </div>

            <div className="bg-yellow-200 border border-gray-100 rounded-lg p-5 flex items-center gap-3 text-left">
              <HelpCircle className="w-6 h-6 text-primary shrink-0" />
              <div>
                <p className="text-xs font-bold text-secondary">Need Clarification?</p>
                <p className="text-[11px] text-gray-500 mt-0.5">Contact HR or your Manager.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ==========================================
  // SCENARIO 3: ACTIVE KRA DASHBOARD
  // ==========================================
  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-lg md:text-2xl font-bold text-gray-900 tracking-tight text-left">KRA Dashboard (FY {selectedYear})</h1>
          <p className="text-xs text-gray-400 mt-1 font-semibold text-left">Performance Cycle: <span className="text-primary">{formatDate(kraData?.startDate)} - {formatDate(kraData?.endDate)}</span></p>
        </div>
        <div className="flex flex-wrap gap-2 items-center self-end md:self-auto">
          <YearFilter />
          <button className="px-4 py-2 border rounded-lg text-xs font-bold text-gray-600 hover:bg-gray-100 uppercase tracking-wide">
            Export Report
          </button>
          {kraData && (
            <button 
              onClick={() => setViewState("review_readonly")}
              className="flex items-center gap-1.5 bg-primary hover:bg-secondary text-white px-4 py-2 rounded-lg font-bold text-xs uppercase hover:shadow-lg transition-all"
            >
              <ShieldCheck className="w-4 h-4" /> View Goals & Acknowledge
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Card 1: Total Progress */}
        <div className="bg-white border hover:border-2 hover:border-secondary hover:shadow-xl rounded-lg p-5 shadow-sm flex items-center justify-between gap-4 text-left">
          <div className="space-y-2">
            <span className="text-[10px] bg-green-50 text-green-700 font-bold px-2 py-0.5 rounded border border-green-200 uppercase">Active Cycle</span>
            <h3 className="text-2xl font-bold text-gray-900">{overallProgress}%</h3>
            <p className="text-[10px] text-gray-400 font-semibold uppercase tracking-wider">
              Ends in {calculateRemainingDays(kraData.endDate)}
            </p>
          </div>
          <div className="relative h-12 w-12 flex items-center justify-center rounded-full border-4 border-primary/20 shrink-0">
            <div className="absolute inset-0 rounded-full border-4 border-primary border-t-transparent animate-spin-slow" />
            <span className="text-[10px] font-bold text-primary">{overallProgress}%</span>
          </div>
        </div>

        {/* Card 2: Active Goals Count */}
        <div className="bg-white border hover:border-2 hover:border-secondary hover:shadow-xl rounded-lg p-5 shadow-sm space-y-4 text-left">
          <div>
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">KRA Items</p>
            <h3 className="text-xl font-bold text-gray-800 mt-1">{kraData.kras?.length || 0} Active</h3>
          </div>
          <div className="w-full bg-gray-100 h-1.5 rounded-full overflow-hidden">
            <div className="bg-primary h-full rounded-full" style={{ width: `${overallProgress}%` }} />
          </div>
        </div>

        {/* Card 3: Points Earned */}
        <div className="bg-white border hover:border-2 hover:border-secondary hover:shadow-xl rounded-lg p-5 shadow-sm flex items-center gap-3 text-left">
          <div className="p-3 bg-red-50 rounded-xl text-primary shrink-0"><Award className="w-6 h-6" /></div>
          <div>
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Points Earned</p>
            <h3 className="text-lg font-bold text-gray-800">1,250 pts</h3>
          </div>
        </div>

        {/* Card 4: Manager Status */}
        <div className="bg-white border hover:border-2 hover:border-secondary hover:shadow-xl rounded-lg p-5 shadow-sm flex items-center gap-3 text-left">
          <div className="p-3 bg-red-50 rounded-xl text-primary shrink-0"><TrendingUp className="w-6 h-6" /></div>
          <div>
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Manager Review</p>
            <h3 className="text-base font-bold text-gray-800">Pending</h3>
            <p className="text-[9px] text-gray-400 font-semibold mt-0.5">Next Sync: June 15th</p>
          </div>
        </div>
      </div>

      {/* Core Responsibilities Section */}
      <div className="space-y-4 pt-2 text-left">
        <h3 className="text-base font-bold text-gray-700 uppercase">Core Responsibilities</h3>
        <div className="space-y-4">
          {kraData.kras?.map((kra, idx) => (
            <div key={idx} className="bg-white border border-gray-100 rounded-lg p-5 hover:border-primary transition-all flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-start min-w-0 flex-1">
                <div className="p-3 bg-red-50 rounded-xl text-primary shrink-0 hidden xs:block">
                  <FileText className="w-5 h-5" />
                </div>
                <div className="sm:ml-4 min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <span className="font-bold text-gray-900 text-sm leading-tight">{kra.name}</span>
                    <span className="text-[9px] bg-red-50 text-primary font-bold px-2 py-0.5 rounded-full uppercase border border-red-100">
                      Weight: {kra.weightage}%
                    </span>
                    <span className="text-[9px] bg-slate-50 text-slate-500 border border-slate-200 font-bold px-2 py-0.5 rounded-full uppercase">
                      Review Frequency: {formatCycleDisplay(kra.reviewCycle)}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 leading-relaxed truncate-2-lines">{kra.description}</p>
                  
                  {/* Simplified target details visualizer showing Target and Dynamic Goal Direction */}
                  {/* ✅ Upgraded targets list to display both Target & Goal Direction clearly */}
                  <div className="flex flex-wrap gap-x-4 gap-y-2 mt-2 text-[10px] text-gray-400 font-bold bg-slate-50/50 p-2.5 rounded-lg max-w-[420px]">
                    <span>Target: <span className="text-gray-950 font-black">{kra.targetValue || kra.monthlyTarget || kra.yearlyTarget || "N/A"}</span></span>
                    <span className="border-l pl-4">Goal Direction: <span className="text-primary uppercase">{kra.metricType === "LOWER_IS_BETTER" ? "Lower is Better" : "Higher is Better"}</span></span>
                  </div>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-300 hidden sm:block shrink-0" />
            </div>
          ))}

          {/* Self-Assessment Promo Banner */}
          <div className="bg-gradient-to-r from-primary to-secondary text-white rounded-xl p-6 md:p-8 flex flex-col sm:flex-row justify-between items-center gap-6 shadow-md shadow-red-50">
            <div className="space-y-2 text-center sm:text-left">
              <h4 className="text-base font-bold leading-tight">Ready for self-assessment?</h4>
              <p className="text-xs text-red-100 max-w-xl">
                You have completed {overallProgress}% of your objectives. You can now start drafting your mid-term review comments for the upcoming performance discussion.
              </p>
            </div>
            <button className="bg-white text-primary hover:shadow-lg hover:bg-yellow-600 hover:text-white hover:shadow-yellow-700/20 transition-all px-6 py-3 rounded-lg font-bold text-xs uppercase shrink-0">
              Start Draft
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}